import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Message, SpinnerService } from 'shared-components';
import { AdminService } from '../../../../services/admin.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-mec-pais-denominacion-new-edit',
    templateUrl: './new-edit.component.html'
})
export class EcPaisDenominacionNewEditComponent implements OnInit, OnDestroy {

    public newEditForm: UntypedFormGroup;

    public showError: boolean;
    public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
    public mensajes = new Array<Message>();

    public deleteModal = false;

    private readonly destroy$ = new Subject<void>();

    private _creating: boolean;
    private _editing: boolean;
    private _toDelete: any;

    public paisId;
    public denominacionId;

    public denominaciones;

    public toCloseSpinner = 0;

    public disabledPaises: Subject<boolean> = new Subject();
    public disabledDenominaciones: Subject<boolean> = new Subject();

    get creating(): boolean { return this._creating; }
    @Input() set creating(value: boolean) {
        this._creating = value;
        if (this.newEditForm) {
            if (value) {
                this.newEditForm.enable();
                this.disabledPaises.next(false);
                this.disabledDenominaciones.next(true);
            } else {
                this.newEditForm.disable();
                this.disabledPaises.next(true);
                this.disabledDenominaciones.next(true);
                this.newEditForm.patchValue({
                    pais: this.paises.find(x => x.id === this.entidadControlPaisDenominacion.paisId),
                    denominacion: this.denominaciones.find(x => x.id === this.entidadControlPaisDenominacion.denominacionId),
                    etiquetaDigital: this.entidadControlPaisDenominacion?.habilitarEtqDigital ?? false
                });
            }
        }
    }

    // El padre indica qué fila está en edición, así solo puede haber una a la vez
    get editing(): boolean { return this._editing; }
    @Input() set editing(value: boolean) {
        this._editing = value;
        const etiquetaDigital = this.newEditForm?.get('etiquetaDigital');
        if (etiquetaDigital) {
            if (value) {
                etiquetaDigital.enable();
            } else {
                etiquetaDigital.patchValue(this.entidadControlPaisDenominacion?.habilitarEtqDigital ?? false);
                etiquetaDigital.disable();
            }
        }
    }

    @Input() entidadControlId;
    @Input() paises;
    @Input() entidadControlPaisDenominacion?;

    @Output() refresh = new EventEmitter<boolean>();
    @Output() editableChanged = new EventEmitter<any>();
    @Output() pushMessage = new EventEmitter<string>();
    @Output() close = new EventEmitter<boolean>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _spinnerService: SpinnerService,
        private readonly _adminService: AdminService,
        private readonly _translate: TranslateService
    ) { }

    ngOnInit() {
        this.newEditForm = this._fb.group({
            pais: [{ value: '', disabled: !this.creating }, Validators.required],
            denominacion: [{ value: '', disabled: !this.creating }, Validators.required],
            etiquetaDigital: [{ value: false, disabled: !this.creating }]
        });

        this.newEditForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                let filters = {
                    paisId: pais.id
                };
                this._spinnerService.showSpinner();
                this.toCloseSpinner += 1;
                this._adminService.getDenominaciones(filters).toPromise().finally(() => {
                    this.checkCloseSpinner();
                }).then((resp) => {
                    if (resp.lista.length > 0) {
                        this.denominaciones = resp.lista;
                        this.newEditForm.get('denominacion').enable();
                        this.disabledDenominaciones.next(false);
                        if (this.entidadControlPaisDenominacion) {
                            this.newEditForm.get('denominacion').disable();
                            this.disabledDenominaciones.next(true);
                            this.newEditForm.get('denominacion').patchValue(this.denominaciones.find(x => x.id ===
                                this.entidadControlPaisDenominacion.denominacionId));
                        }
                    }
                }, (error) => {
                    this.showApiError = true;
                    if (error.status === 420) {
                        this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
                    } else {
                        this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                    }
                });
            } else {
                this.newEditForm.get('denominacion').disable();
                this.newEditForm.get('denominacion').patchValue('');
                this.disabledDenominaciones.next(true);
                this.denominaciones = [];
            }
        });

        if (this.entidadControlPaisDenominacion) {
            this.newEditForm.patchValue({
                pais: this.paises.find(x => x.id === this.entidadControlPaisDenominacion.paisId),
                etiquetaDigital: this.entidadControlPaisDenominacion.habilitarEtqDigital ?? false
            });
            this.newEditForm.disable();
            this.disabledPaises.next(true);
            this.disabledDenominaciones.next(true);
        } else {
            this.newEditForm.enable();
            this.newEditForm.get('denominacion').disable();
            this.disabledPaises.next(false);
            this.disabledDenominaciones.next(true);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ID único para cada checkbox
    get etiquetaDigitalId(): string {
        return this.entidadControlPaisDenominacion
            ? 'admin-etiqueta-digital-' + this.entidadControlPaisDenominacion.paisId + '-' + this.entidadControlPaisDenominacion.denominacionId
            : 'admin-etiqueta-digital-new';
    }

    private updateEtiquetaDigital(editEcPaisDenominacion) {
        this._spinnerService.showSpinner();
        this._adminService.updateEntidadControlPaisDenominacion(editEcPaisDenominacion).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.entidadControlPaisDenominacion.habilitarEtqDigital = editEcPaisDenominacion.habilitarEtqDigital;
            this.editableChanged.emit(false);
            this.refresh.emit(true);
            this.pushMessage.emit('global.success.itemUpdated');
        },
        (error) => {
            this.showError = true;
            const message = this._translate.instant('ApiErrors.serverError');
            this.pushMessage.emit(message);
        });
    }

    public save() {
        this.checkForm();
        if (this.newEditForm.valid) {
            const ecPaisDenominacion = {
                entidadControlId: this.entidadControlId,
                paisId: this.newEditForm.get('pais').value?.id,
                denominacionId: this.newEditForm.get('denominacion').value?.id,
                habilitarEtqDigital: this.newEditForm.get('etiquetaDigital').value
            };
            // Creación
            if (!this.entidadControlPaisDenominacion) {
                this._spinnerService.showSpinner();
                this._adminService.createEntidadControlPaisDenominacion(ecPaisDenominacion).toPromise().finally(() => {
                    this._spinnerService.hideSpinner();
                }).then((resp) => {
                    if (resp) {
                        this._creating = false;
                        this.refresh.emit(true);
                        this.newEditForm.disable();
                        this.disabledDenominaciones.next(true);
                        this.disabledPaises.next(true);
                        this.pushMessage.emit('global.success.itemCreated');
                    }
                },
                (error) => {
                    this.showError = true;
                    let message;
                    if (error.status === 420) {
                      switch (error.error.ApiErrorCode) {
                        case 3001:
                          message = this._translate.instant('ApiErrors.paisDenoYaExiste');
                          break;
                        default:
                            message = this._translate.instant('ApiErrors.serverError');
                          break;
                      }
                    } else {
                        message = this._translate.instant('ApiErrors.serverError');
                    }
                    this.pushMessage.emit(message);
                  }
                );
            } else {
                // Edición: solo se modifica la etiqueta digital
                this.updateEtiquetaDigital(ecPaisDenominacion);
            }
        }
    }

    public edit() {
        // El padre marca esta fila como editable y cierra la fila de creación
        this.editableChanged.emit(this.entidadControlPaisDenominacion);
    }

    public cancel() {
        if (!this.entidadControlPaisDenominacion) {
            this.newEditForm.reset();
        }
        this.editableChanged.emit(false);
    }

    public askDelete() {
        window.event.stopPropagation();
        this.deleteModal = true;
    }

    public delete() {
        this._spinnerService.showSpinner();
        this._adminService.deleteEntidadControlPaisDenominacion(this.entidadControlId, this.entidadControlPaisDenominacion.paisId,
            this.entidadControlPaisDenominacion.denominacionId).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then(() => {
                this.closeModal();
                this.refresh.emit(true);
                this.pushMessage.emit('global.success.itemDeleted');
            });
    }

    public closeModal() {
        this.deleteModal = false;
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    private checkForm() {
        this.showError = this.newEditForm.invalid;
        if (this.showError) {
            this.pushMessage.emit('global.error.required');
        }
    }
}

